import * as store from '@attain-apps/fe/web/main/lib/store'

import type * as models from './models'
import type { State } from './state'

/**
 * Core actions for this feature
 */
// NOTE: intentionally missing return type so that type can be inferred
export function getActionsConfig() {
  return {
    /**
     * Asks a question to the assistant
     */
    ask: store.actions.request({
      input: store.actions.Data.asIs<models.Ask.Request>(),
      output: ({
        replies = [],
      }: models.Ask.Response = {}): Required<models.Ask.Response> => ({
        replies,
      }),
      reducers: {
        cancellation: ({ state }) => ({
          ...state,
          ask: { isSubmitting: false },
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          ask: { error, isSubmitting: false },
        }),
        request: ({ state, data: { question } }) => ({
          ...state,
          ask: { isSubmitting: true },
          messages: [
            ...state.messages,
            {
              content: question,
              from: 'user',
            },
          ],
        }),
        success: ({
          state,
          data: {
            response: { replies },
          },
        }): State => ({
          ...state,
          ask: { isSubmitting: false },
          messages: [
            ...state.messages,
            ...replies.map<models.Message>(content => ({
              content,
              from: 'assistant',
            })),
          ],
        }),
      },
    }),
    /**
     * Retrieves suggested questions
     */
    getSuggestedQuestions: store.actions.request({
      input: store.actions.Data.NONE,
      output: store.actions.Data.asIs<Pick<State, 'suggestedQuestions'>>(),
      reducers: {
        cancellation: ({ state }) => ({
          ...state,
          suggestedQuestionsRequest: { isLoading: false },
        }),
        error: ({ state, data: { error } }) => ({
          ...state,
          suggestedQuestionsRequest: { error, isLoading: false },
        }),
        request: ({ state }) => ({
          ...state,
          suggestedQuestionsRequest: { isLoading: true },
        }),
        success: ({
          state,
          data: {
            response: { suggestedQuestions },
          },
        }): State => ({
          ...state,
          suggestedQuestions,
          suggestedQuestionsRequest: { isLoading: false },
        }),
      },
    }),
    /**
     * Records {@link models.Message messages} into the chat with the assistant
     */
    record: store.actions.standalone({
      data: store.actions.Data.asIs<{
        readonly messages: readonly models.Message[]
      }>(),
      reducer: ({ state, data: { messages } }) => ({
        ...state,
        messages: [...state.messages, ...messages],
      }),
    }),
    /**
     * Clears all {@link models.Message messages} in the chat with the assistant
     */
    reset: store.actions.standalone({
      data: store.actions.Data.NONE,
      // Removes errors and clears messages
      reducer: ({ state }) => ({
        ...state,
        ask: { ...state.ask, error: undefined },
        messages: [],
        suggestedQuestionsRequest: {
          ...state.suggestedQuestionsRequest,
          error: undefined,
        },
      }),
    }),
  } as const satisfies store.actions.Config.Constraint<State>
}
